
import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from '@/hooks/use-mobile';

interface ChatBubbleProps {
  message: string;
  isOutgoing: boolean;
  timestamp: Date;
  isRead?: boolean;
  className?: string;
}

const ChatBubble: React.FC<ChatBubbleProps> = ({
  message,
  isOutgoing,
  timestamp,
  isRead = false,
  className
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const isMobile = useIsMobile();
  
  // Animation variants
  const bubbleVariants = {
    initial: { 
      opacity: 0, 
      x: isOutgoing ? 20 : -20,
      scale: 0.9 
    },
    animate: { 
      opacity: 1, 
      x: 0,
      scale: 1,
      transition: { 
        type: 'spring', 
        damping: 15, 
        stiffness: 300 
      }
    },
    hover: {
      scale: isMobile ? 1.01 : 1.02,
      boxShadow: isOutgoing 
        ? '0 4px 12px rgba(108, 65, 224, 0.2)' 
        : '0 4px 12px rgba(0, 0, 0, 0.1)',
      transition: { duration: 0.2 }
    }
  };

  // Format time for display in tooltip
  const formattedTime = format(timestamp, 'h:mm a');
  const formattedDate = format(timestamp, 'MMM d, yyyy');

  return (
    <div className={cn(
      'flex flex-col mb-2.5',
      isMobile ? 'max-w-[85%]' : 'max-w-[80%]',
      isOutgoing ? 'ml-auto' : 'mr-auto',
      className
    )}>
      <TooltipProvider>
        <Tooltip delayDuration={isMobile ? 500 : 300}>
          <TooltipTrigger asChild>
            <motion.div 
              initial="initial"
              animate="animate"
              whileHover="hover"
              variants={bubbleVariants}
              onHoverStart={() => setIsHovered(true)}
              onHoverEnd={() => setIsHovered(false)}
              className={cn(
                'p-2.5 rounded-2xl shadow-sm transition-shadow',
                isOutgoing ? 
                  'bg-gradient-to-br from-[#6C41E0]/90 to-[#5B35C0] text-white rounded-tr-none' : 
                  'bg-[#292D67] text-white rounded-tl-none',
                isHovered && 'shadow-md'
              )}
            >
              <p className={cn(
                "break-words",
                isMobile ? "text-xs" : "text-sm"
              )}>
                {message}
              </p>
              
              <div className="time-stamp mt-1 flex justify-end">
                <time className="text-[10px] text-white/50">
                  {format(timestamp, 'HH:mm')}
                </time>
              </div>
            </motion.div>
          </TooltipTrigger>
          <TooltipContent side={isOutgoing ? "left" : "right"} className="backdrop-blur-md bg-background/90">
            <p className="text-xs">
              {formattedTime} <br />
              {formattedDate}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      <div className={cn(
        "flex items-center mt-0.5",
        isOutgoing ? "ml-auto" : "mr-auto"
      )}>
        {isOutgoing && (
          <motion.div 
            className="ml-1"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 500 }}
          >
            <CheckCircle2 
              size={isMobile ? 10 : 12}
              className={cn(
                "text-white/60",
                isRead && "text-blue-400"
              )} 
              fill={isRead ? "#60A5FA" : "none"} 
            />
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ChatBubble;
